import {
  avancarCopa, campanhaNaCopa, type FaseCopa, COPA_NOME, ehRodadaDeCopa, iniciarCopa, iniciarCopaJunta, MESES_DE_FOLHA_COPA, PATROCINIO_COPA, RODADAS_COPA, rodadasDeLiga, temCopaJunta, type Copa,
} from './copa';
import { calcularBilheteria, calcularSalario, calcularValor, folhaPorRodada, PATROCINIO_TEMPORADA, PREMIO_CAMPEAO_SERIE_A, premioColocacao } from './financas';
import { avancarLibertadores, brasileirosQualificados, campanhaNaLibertadores, campanhaNaSulamericana, classificacaoDoGrupo, clubesComExternos, clubeExterno, CORTES_CONTINENTAIS, dividirVagas, ehRodadaDeLibertadores, iniciarContinentais, LIBERTADORES_NOME, melhoresBrasileiros, SULAMERICANA_NOME, type Libertadores } from './libertadores';
import {
  completarJuniores, contratoInicial, cobrarJuros, evoluirJuniores, JUNIORES_INICIAIS, NOME_LIGA, NOME_SERIE_B, passarDiasDeContrato, registrarTrofeu, virarAnoJuniores,
} from './extras';
import { gerarJogador, gerarLiga, gerarLigaSerieA2026, gerarLigaSerieB2026 } from './gerador';
import { ELENCO_MAX } from './mercado';
import { simularLigasMundo, type TabelaMundo } from './ligas-mundo-sim';
import { avancarLigasMundo, finalizarLigasMundo, iniciarLigasMundoVivas, rodadasDaLiga, type LigaMundoViva } from './ligas-mundo-calendario';
import { nomeDaLiga } from './ligas-mundo';
import { calendarioLigaMundo, DIVISOES_MUNDO, gerarLigaMundo, gerarMundo, ID_BASE_MUNDO, ligaMundoPorId, ligasDosClubes } from './liga-mundo-jogavel';
import { nomeJogador } from './nomes';
import { calcularTabela, gerarCalendario, type Calendario, type LinhaTabela, type ResultadoJogo } from './liga';
import { mercadoIA, reporLivres, type Transferencia } from './mercado';
import { simularPartida, type IntervencaoPartida, type ResultadoPartida } from './partida';
import { Rng } from './rng';
import { TATICA_PADRAO } from './tatica';
import { definirModoHabilidade } from './habilidades';
import { treinarClube } from './treino';
import { validarSave } from './validar-save';
import type { Clube, DivisaoClube, Jogador, Trofeu } from './tipos';

/**
 * v2: tática por clube, jogadores livres, histórico de transferências e `balanco.transferencias`.
 * v3: assistências, notas, lado e características dos jogadores; confiança da diretoria/torcida, preços sugeridos,
 *     marcação e concentração de ataque; eventos de partida com minuto.
 * v4: contratos com dias restantes e multa de rescisão, empréstimo bancário, academia de juniores, galeria de troféus,
 *     detalhamento do balanço (vendas, compras, estádio, multas, juros).
 */
export const VERSAO_SAVE = 4;

export interface PartidaJogada {
  mandante: number;
  visitante: number;
  resultado: ResultadoPartida;
  publico: number;
  renda: number;
}

export interface RodadaJogada {
  temporada: number;
  /** índice 0-based da rodada */
  rodada: number;
  partidas: PartidaJogada[];
  /** contratos do clube humano renovados automaticamente nesta rodada */
  contratosRenovados?: string[];
  /** evoluções e lesões de treino dos clubes humanos nesta rodada */
  treino?: string[];
}

export interface HistoricoTemporada {
  temporada: number;
  campeaoId: number;
  posicaoHumano: number;
  /** competição disputada ('Copa do Brasil' ou omitido para a liga) e até onde o clube humano foi na copa */
  competicao?: string;
  fase?: string;
  /** no save com Brasileirão + Copa do Brasil: campeão da copa da temporada e campanha do clube humano nela */
  copaCampeaoId?: number;
  copaFase?: string;
  /** com a Libertadores no save: campeão da temporada e campanha do clube humano */
  libCampeaoId?: number;
  libFase?: string;
  /** com a Sul-Americana no save: campeão da temporada e campanha do clube humano */
  sulCampeaoId?: number;
  sulFase?: string;
  /** o que as Tabelas mostram ao escolher uma temporada que já passou: classificações finais, chaveamentos e grupos da Libertadores */
  tabelaA?: LinhaTabela[];
  tabelaB?: LinhaTabela[];
  copaFases?: FaseCopa[];
  libGrupos?: LinhaTabela[][];
  libFases?: FaseCopa[];
  sulGrupos?: LinhaTabela[][];
  sulFases?: FaseCopa[];
  /** jogos com acesso e rebaixamento: campeões [Série A, Série B] e os clubes que caíram e subiram nesta temporada */
  campeoes?: [number, number];
  rebaixados?: number[];
  promovidos?: number[];
  /** jogo com o mundo inteiro: clubes estrangeiros que caíram e que subiram nesta temporada (nas ligas que têm divisão de acesso) */
  rebMundo?: number[];
  promMundo?: number[];
}

/** A Série B paga uma fração dos prêmios de colocação da Série A. */
const PREMIO_SERIE_B = 0.4;

export interface EstadoJogo {
  versao: number;
  semente: number;
  rngEstado: number;
  temporada: number;
  /** próxima rodada a jogar (0-based) */
  rodada: number;
  humanoId: number;
  proximoIdJogador: number;
  clubes: Clube[];
  calendario: Calendario;
  resultados: ResultadoJogo[];
  ultimaRodada: RodadaJogada | null;
  historico: HistoricoTemporada[];
  /** jogadores sem clube, disponíveis para contratação */
  livres: Jogador[];
  /** ids (de jogadoresExternos) dos jogadores de ligas estrangeiras já contratados neste jogo */
  externosComprados?: number[];
  /** últimas transferências (de qualquer clube) */
  transferencias: Transferencia[];
  /** nome do técnico humano (só exibição) */
  tecnico?: string;
  /** títulos conquistados pelo técnico humano (galeria de troféus do técnico) */
  trofeusTecnico?: Trofeu[];
  /** 'liga' (padrão) ou 'copa' (mata-mata; o calendário cresce a cada fase) */
  formato?: 'liga' | 'copa';
  copa?: Copa;
  /** Copa Libertadores no mesmo save (só no jogo solo com Série A + Copa do Brasil; ver libertadores.ts) */
  libertadores?: Libertadores;
  /** Copa Sul-Americana (nasce junto com a Libertadores; usa os clubes estrangeiros dela) */
  sulamericana?: Libertadores;
  /** o jogo não quer a Libertadores: ela não entra sozinha na virada da temporada (escolha do técnico no jogo solo novo) */
  semLibertadores?: boolean;
  /** classificação final da última temporada simulada de cada liga estrangeira (id de ligas-mundo.ts); ver ligas-mundo-sim.ts */
  ligasMundo?: Record<string, TabelaMundo>;
  /** temporada em andamento de cada liga estrangeira, jogada junto com as rodadas de liga do Brasileirão (ver ligas-mundo-calendario.ts) */
  ligasMundoVivas?: Record<string, LigaMundoViva>;
  /** @deprecated a força pela média das habilidades da posição agora é sempre usada; o campo só existe em saves antigos */
  habilidadeIndividual?: boolean;
  /** divisão do Brasileirão disputada: 'b' = Série B; ausente = Série A */
  divisao?: 'a' | 'b';
  /**
   * Série A e Série B no mesmo jogo (40 clubes, cada um com a sua `divisao`): as duas ligas rodam juntas a cada rodada e, no fim da
   * temporada, os 4 últimos da Série A caem e os 4 primeiros da Série B sobem. Sem isto (saves antigos), só uma divisão existe.
   */
  acesso?: boolean;
  /** jogo numa liga estrangeira (id de ligas-mundo.ts): os clubes dela são os do save, em pontos corridos, sem copas */
  ligaMundo?: string;
  /** nome da liga do jogo, mostrado no lugar de "Brasileirão" (só nos jogos de liga estrangeira) */
  nomeLiga?: string;
  /**
   * Jogo com o mundo inteiro: além do Brasileirão (A + B, com copas), as 13 ligas estrangeiras têm os clubes no save (campo `liga`),
   * jogam junto com o motor completo e as que têm segunda divisão têm acesso e rebaixamento (ver DIVISOES_MUNDO).
   */
  mundo?: boolean;
  /** prêmios de colocação das ligas já pagos na temporada (chaves "temporada|liga"): pagos quando a liga termina, não só no encerramento */
  premiosPagos?: string[];
  /** jogo solo: true desliga a demissão por caixa em -R$ 10 milhões ou pior ao fim da temporada (opção em Opções; ligada por padrão) */
  semDemissaoPorCaixa?: boolean;
}

/** Quantos clubes sobem e descem a cada temporada. */
export const VAGAS_ACESSO = 4;

/** Divisão (1 = Série A, 2 = Série B) do clube; sem acesso e rebaixamento, a única divisão do jogo. */
export function divisaoDoClube(e: EstadoJogo, c: Clube): DivisaoClube {
  return c.divisao ?? (e.divisao === 'b' ? 2 : 1);
}

/** Divisão do clube do técnico humano. */
export function divisaoDoHumano(e: EstadoJogo): DivisaoClube {
  const c = e.clubes.find((x) => x.id === e.humanoId);
  return c ? divisaoDoClube(e, c) : e.divisao === 'b' ? 2 : 1;
}

/** Clubes brasileiros de uma divisão (os de liga estrangeira, quando há, ficam de fora). */
export function clubesDaDivisao(e: EstadoJogo, divisao: DivisaoClube): Clube[] {
  return e.clubes.filter((c) => !(e.mundo && c.liga) && divisaoDoClube(e, c) === divisao);
}

/** Liga estrangeira do clube do técnico num jogo com o mundo inteiro (undefined: ele está no Brasileirão). */
export function ligaDoHumano(e: EstadoJogo): string | undefined {
  return e.mundo ? e.clubes.find((c) => c.id === e.humanoId)?.liga : undefined;
}

/** Classificação de uma liga estrangeira (jogo com o mundo inteiro): os clubes que estão nela agora. */
export function tabelaDaLiga(e: EstadoJogo, ligaId: string): LinhaTabela[] {
  return calcularTabela(
    e.clubes.filter((c) => c.liga === ligaId).map((c) => c.id),
    e.resultados.filter((r) => !ehRodadaDeCopa(e, r.rodada) && !ehRodadaDeLibertadores(e, r.rodada)),
  );
}

/** Clubes que o técnico pode escolher numa divisão (jogos de uma divisão só: todos). */
export function clubesParaEscolher(e: EstadoJogo, divisao: DivisaoClube): Clube[] {
  return e.acesso ? clubesDaDivisao(e, divisao) : [...e.clubes];
}

export function nomeDaDivisao(divisao: DivisaoClube): string {
  return divisao === 2 ? NOME_SERIE_B : NOME_LIGA;
}

/** Calendário das duas divisões juntas: a rodada i tem os jogos da Série A e da Série B (cada uma com o seu sorteio). */
function calendarioDasDivisoes(e: EstadoJogo, rng: Rng): Calendario {
  const a = gerarCalendario(clubesDaDivisao(e, 1).map((c) => c.id), rng);
  const b = gerarCalendario(clubesDaDivisao(e, 2).map((c) => c.id), rng);
  return a.map((r, i) => [...r, ...(b[i] ?? [])]);
}

/**
 * Calendário de liga do save (sem as copas): Brasileirão A + B, ou a liga única, e, no jogo com o mundo inteiro, o de cada liga
 * estrangeira junto (a rodada i tem os jogos da rodada i de todas as ligas; as que têm menos rodadas terminam antes).
 */
function calendarioDeLiga(e: EstadoJogo, rng: Rng): Calendario {
  const base = e.acesso
    ? calendarioDasDivisoes(e, rng)
    : e.ligaMundo
      ? calendarioLigaMundo(e.clubes.map((c) => c.id), rng)
      : gerarCalendario(e.clubes.map((c) => c.id), rng);
  if (!e.mundo) return base;
  for (const liga of ligasDosClubes(e.clubes)) {
    const cal = calendarioLigaMundo(e.clubes.filter((c) => c.liga === liga).map((c) => c.id), rng);
    cal.forEach((rodada, i) => {
      (base[i] ??= []).push(...rodada);
    });
  }
  return base;
}

/** `comCopa`: Brasileirão e Copa do Brasil no mesmo save (a copa entra entre as rodadas da liga). Exige 16 clubes ou mais. */
export function novoJogo(
  semente: number,
  humanoId = 0,
  quantidadeClubes = 20,
  divisao: 'a' | 'b' = 'a',
  comCopa = false,
  /** Série A e Série B juntas, com acesso e rebaixamento (ver EstadoJogo.acesso); os clubes da Série A têm id 0 a 19 e os da B, 20 a 39 */
  comAcesso = false,
  /** Libertadores no mesmo save (exige copa e acesso); só no jogo solo por enquanto */
  comLibertadores = false,
  /** as 13 ligas estrangeiras no mesmo save, com os clubes de verdade (exige acesso); ver EstadoJogo.mundo */
  comMundo = false,
): EstadoJogo {
  const rng = new Rng(semente);
  const contador = { valor: 1 };
  const duplo = comAcesso && quantidadeClubes === 20;
  const mundo = comMundo && duplo;
  const clubes = duplo
    ? [...gerarLigaSerieA2026(rng, contador), ...gerarLigaSerieB2026(rng, contador, 20), ...(mundo ? gerarMundo(rng, contador) : [])]
    : quantidadeClubes !== 20
      ? gerarLiga(quantidadeClubes, rng, contador)
      : divisao === 'b'
        ? gerarLigaSerieB2026(rng, contador)
        : gerarLigaSerieA2026(rng, contador);
  if (duplo) for (const c of clubes) if (c.id < ID_BASE_MUNDO) c.divisao = c.id < 20 ? 1 : 2;
  const humano = clubes.find((c) => c.id === humanoId) ?? (clubes[0] as Clube);
  humano.humano = true;
  const calendario = duplo
    ? mundo
      ? [] // o calendário do mundo inteiro é montado logo abaixo, com o estado já marcado como `mundo`
      : calendarioDasDivisoes({ clubes, divisao } as EstadoJogo, rng)
    : gerarCalendario(
        clubes.map((c) => c.id),
        rng,
      );
  const estado: EstadoJogo = {
    versao: VERSAO_SAVE,
    semente,
    rngEstado: 0,
    temporada: 1,
    rodada: 0,
    humanoId: humano.id,
    proximoIdJogador: contador.valor,
    clubes,
    calendario,
    resultados: [],
    ultimaRodada: null,
    historico: [],
    livres: [],
    transferencias: [],
    ...(divisao === 'b' && !duplo ? { divisao: 'b' as const } : {}),
    ...(duplo ? { acesso: true } : {}),
    ...(mundo ? { mundo: true } : {}),
  };
  if (mundo) estado.calendario = calendarioDeLiga(estado, rng);
  reporLivres(estado, rng);
  const proximoId = { valor: estado.proximoIdJogador };
  for (const c of clubes) {
    for (const j of c.elenco) j.contratoDias = contratoInicial(j.id);
    c.emprestimo = 0;
    c.trofeus = [];
    c.juniores = [];
    completarJuniores(c, JUNIORES_INICIAIS, rng, proximoId, nomeJogador);
  }
  estado.proximoIdJogador = proximoId.valor;
  estado.trofeusTecnico = [];
  if (duplo && !mundo) iniciarLigasMundoVivas(estado);
  if (comCopa && clubes.length >= 16) {
    const extras = iniciarCopaJunta(estado, rng, estado.calendario, comLibertadores && duplo ? CORTES_CONTINENTAIS : []);
    if (extras.length > 0) iniciarContinentais(estado, rng, dividirVagas(melhoresBrasileiros(estado)), extras);
  }
  estado.rngEstado = rng.estado;
  return estado;
}

/**
 * Jogo novo numa liga estrangeira (id de ligas-mundo.ts): clubes e jogadores reais da planilha, pontos corridos, sem copas.
 * O clube do técnico é escolhido depois (como no jogo brasileiro, `humanoId` e `humano` mudam ao iniciar).
 */
export function novoJogoLiga(semente: number, ligaId: string, humanoId = 0): EstadoJogo {
  const info = ligaMundoPorId(ligaId);
  if (!info) throw new Error(`liga desconhecida: ${ligaId}`);
  const rng = new Rng(semente);
  const contador = { valor: 1 };
  const clubes = gerarLigaMundo(ligaId, rng, contador);
  const humano = clubes.find((c) => c.id === humanoId) ?? (clubes[0] as Clube);
  humano.humano = true;
  const calendario = calendarioLigaMundo(clubes.map((c) => c.id), rng);
  const estado: EstadoJogo = {
    versao: VERSAO_SAVE,
    semente,
    rngEstado: 0,
    temporada: 1,
    rodada: 0,
    humanoId: humano.id,
    proximoIdJogador: contador.valor,
    clubes,
    calendario,
    resultados: [],
    ultimaRodada: null,
    historico: [],
    livres: [],
    transferencias: [],
    ligaMundo: ligaId,
    nomeLiga: info.nome,
  };
  reporLivres(estado, rng);
  const proximoId = { valor: estado.proximoIdJogador };
  for (const c of clubes) {
    for (const j of c.elenco) j.contratoDias = contratoInicial(j.id);
    c.emprestimo = 0;
    c.trofeus = [];
    c.juniores = [];
    completarJuniores(c, JUNIORES_INICIAIS, rng, proximoId, nomeJogador);
  }
  estado.proximoIdJogador = proximoId.valor;
  estado.trofeusTecnico = [];
  estado.rngEstado = rng.estado;
  return estado;
}

/** Jogo novo no formato copa (Copa do Brasil): mesmos clubes e jogadores, calendário de mata-mata. */
export function novoJogoCopa(semente: number, humanoId = 0): EstadoJogo {
  const e = novoJogo(semente, humanoId);
  const rng = new Rng(e.rngEstado);
  iniciarCopa(e, rng);
  e.rngEstado = rng.estado;
  return e;
}

export function clubePorId(e: EstadoJogo, id: number): Clube {
  const c = e.clubes.find((x) => x.id === id) ?? clubeExterno(e, id);
  if (!c) throw new Error(`clube ${id} não existe`);
  return c;
}

export function temporadaEncerrada(e: EstadoJogo): boolean {
  return e.rodada >= e.calendario.length;
}

/**
 * Classificação do campeonato de pontos corridos (os jogos da Copa do Brasil, quando há no mesmo save, não entram).
 * Com as duas divisões no jogo, `divisao` escolhe a tabela (padrão: a do clube do técnico).
 */
export function tabela(e: EstadoJogo, divisao?: DivisaoClube): LinhaTabela[] {
  // técnico num clube estrangeiro (jogo com o mundo inteiro): sem divisão pedida, vale a liga dele
  const ligaHumano = divisao === undefined ? ligaDoHumano(e) : undefined;
  if (ligaHumano) return tabelaDaLiga(e, ligaHumano);
  const clubes = e.acesso ? clubesDaDivisao(e, divisao ?? divisaoDoHumano(e)) : e.clubes;
  return calcularTabela(
    clubes.map((c) => c.id),
    e.resultados.filter((r) => !ehRodadaDeCopa(e, r.rodada) && !ehRodadaDeLibertadores(e, r.rodada)),
  );
}

const ENERGIA_RECUPERACAO = 18;
const ENERGIA_CUSTO_TITULAR = 22;
const ENERGIA_CUSTO_RESERVA = 10;

function jogadorPorId(c: Clube, id: number): Jogador | undefined {
  return c.elenco.find((j) => j.id === id);
}

const limitar = (v: number): number => Math.min(100, Math.max(0, Math.round(v)));

/** Diretoria e torcida reagem ao resultado: vitória sobe a confiança, derrota derruba (a torcida é mais volúvel). */
function atualizarConfianca(c: Clube, gp: number, gc: number): void {
  if (gp > gc) {
    c.confiancaDiretoria = limitar(c.confiancaDiretoria + 2);
    c.confiancaTorcida = limitar(c.confiancaTorcida + 3);
  } else if (gp < gc) {
    c.confiancaDiretoria = limitar(c.confiancaDiretoria - 3);
    c.confiancaTorcida = limitar(c.confiancaTorcida - 4);
  } else {
    c.confiancaTorcida = limitar(c.confiancaTorcida - 1);
  }
}

/** Data de calendário de uma rodada: a temporada 1 é 2026 e começa em 28/01 (abertura real do Brasileirão 2026); há uma rodada por semana. */
export function dataDaRodada(temporada: number, rodada: number): Date {
  return new Date(Date.UTC(2025 + temporada, 0, 28 + rodada * 7));
}

/** Nota média do jogador na temporada, ou null se ainda não jogou. */
export function notaMedia(j: Jogador): number | null {
  return j.jogos > 0 ? Math.round((j.notaSoma / j.jogos) * 100) / 100 : null;
}

/**
 * Deixa em `suspensaoRodadas` e `amarelosAcumulados` de cada jogador a contagem do campeonato da PRÓXIMA rodada (copa ou liga), que é
 * a que vale para escalar: quem foi expulso na liga pode jogar a copa e só perde o próximo jogo da liga (e o contrário).
 * Também migra saves antigos, que só tinham a contagem única (tratada como da liga).
 */
export function sincronizarCartoes(e: EstadoJogo): void {
  const proximaEhLib = e.rodada < e.calendario.length && ehRodadaDeLibertadores(e, e.rodada);
  const proximaEhCopa = e.rodada < e.calendario.length && ehRodadaDeCopa(e, e.rodada) && e.formato !== 'copa';
  for (const c of clubesComExternos(e)) {
    for (const j of c.elenco) {
      if (j.suspLiga === undefined) {
        j.suspLiga = j.suspensaoRodadas;
        j.amarelosLiga = j.amarelosAcumulados;
      }
      j.suspCopa ??= 0;
      j.amarelosLiga ??= 0;
      j.amarelosCopa ??= 0;
      j.suspLib ??= 0;
      j.amarelosLib ??= 0;
      j.suspensaoRodadas = proximaEhLib ? j.suspLib : proximaEhCopa ? j.suspCopa : j.suspLiga;
      j.amarelosAcumulados = proximaEhLib ? j.amarelosLib : proximaEhCopa ? j.amarelosCopa : j.amarelosLiga;
    }
  }
}

/** Foto da temporada que termina (classificações finais, chaveamentos e grupos), para as Tabelas das temporadas passadas. */
function fotografarTemporada(e: EstadoJogo): Pick<HistoricoTemporada, 'tabelaA' | 'tabelaB' | 'copaFases' | 'libGrupos' | 'libFases' | 'sulGrupos' | 'sulFases'> {
  const semCobrancas = (fases: FaseCopa[]): FaseCopa[] => fases.map((f) => ({ ...f, confrontos: f.confrontos.map(({ cobrancas: _c, ...c }) => c) }));
  const foto: Pick<HistoricoTemporada, 'tabelaA' | 'tabelaB' | 'copaFases' | 'libGrupos' | 'libFases' | 'sulGrupos' | 'sulFases'> = {};
  if (e.formato !== 'copa') {
    if (e.acesso) {
      foto.tabelaA = tabela(e, 1);
      foto.tabelaB = tabela(e, 2);
    } else if (divisaoDoHumano(e) === 2) foto.tabelaB = tabela(e);
    else foto.tabelaA = tabela(e);
  }
  if (e.copa) foto.copaFases = semCobrancas(e.copa.fases);
  if (e.libertadores) {
    foto.libGrupos = e.libertadores.grupos.map((g) => classificacaoDoGrupo(e, g));
    foto.libFases = semCobrancas(e.libertadores.fasesMata);
  }
  if (e.sulamericana) {
    foto.sulGrupos = e.sulamericana.grupos.map((g) => classificacaoDoGrupo(e, g, e.sulamericana));
    foto.sulFases = semCobrancas(e.sulamericana.fasesMata);
  }
  return foto;
}

/**
 * Premia os clubes de uma liga pela colocação final (a divisão de baixo paga menos; o campeão da de cima leva também o prêmio do título).
 * Paga uma vez só por liga e temporada: o prêmio sai quando a liga termina, e o encerramento da temporada repete a chamada sem pagar de novo.
 */
function pagarPremiosDaLiga(e: EstadoJogo, chave: string, t: LinhaTabela[], segunda: boolean): void {
  const pagos = (e.premiosPagos ??= []);
  const k = `${e.temporada}|${chave}`;
  if (pagos.includes(k)) return;
  pagos.push(k);
  t.forEach((linha, i) => {
    const c = e.clubes.find((x) => x.id === linha.clubeId);
    if (!c) return;
    const premio = Math.round(premioColocacao(i + 1) * (segunda ? PREMIO_SERIE_B : 1)) + (!segunda && i === 0 ? PREMIO_CAMPEAO_SERIE_A : 0);
    c.caixa += premio;
    c.balanco.premios += premio;
  });
}

/**
 * Bônus de partida do clube (item Bicho): multiplica a força de todos os jogadores (overall e habilidades, no máximo 99) para a partida
 * que está para ser simulada e devolve a função que desfaz, restaurando os valores e gastando o bônus. Sem bônus, não faz nada.
 */
export function aplicarBonusPartida(c: Clube): () => void {
  const bonus = c.bonusPartida ?? 0;
  if (bonus <= 0) return () => undefined;
  const antes = c.elenco.map((j) => ({ j, overall: j.overall, habilidades: j.habilidades ? { ...j.habilidades } : undefined }));
  const f = 1 + bonus;
  for (const { j } of antes) {
    j.overall = Math.min(99, Math.round(j.overall * f));
    if (j.habilidades) for (const k of Object.keys(j.habilidades) as (keyof typeof j.habilidades)[]) j.habilidades[k] = Math.min(99, Math.round(j.habilidades[k] * f));
  }
  return () => {
    for (const a of antes) {
      a.j.overall = a.overall;
      if (a.habilidades) a.j.habilidades = a.habilidades;
    }
    delete c.bonusPartida;
  };
}

/** Troféu do técnico (galeria dele), sem repetir o mesmo título da mesma temporada. */
function registrarTrofeuTecnico(e: EstadoJogo, t: Trofeu): void {
  const lista = (e.trofeusTecnico ??= []);
  if (!lista.some((x) => x.competicao === t.competicao && x.temporada === t.temporada)) lista.push(t);
}

function darTitulo(e: EstadoJogo, clubeId: number | null | undefined, t: Trofeu): void {
  const c = clubeId == null ? undefined : e.clubes.find((x) => x.id === clubeId);
  if (!c) return;
  registrarTrofeu(c, t);
  if (c.id === e.humanoId) registrarTrofeuTecnico(e, t);
}

/**
 * Dá o troféu assim que o campeonato termina (e não só ao encerrar a temporada): as copas, quando a final decide o campeão, e as
 * ligas, quando a última rodada delas é jogada. O encerramento da temporada repete as chamadas e não duplica (ver registrarTrofeu).
 */
function concederTitulosConcluidos(e: EstadoJogo): void {
  if (e.formato === 'copa') return;
  const ano = e.temporada;
  if (e.copa?.campeaoId != null) darTitulo(e, e.copa.campeaoId, { temporada: ano, competicao: COPA_NOME, tipo: 'copa', divisao: 1 });
  if (e.libertadores?.campeaoId != null) darTitulo(e, e.libertadores.campeaoId, { temporada: ano, competicao: LIBERTADORES_NOME, tipo: 'internacional', divisao: 1 });
  if (e.sulamericana?.campeaoId != null) darTitulo(e, e.sulamericana.campeaoId, { temporada: ano, competicao: SULAMERICANA_NOME, tipo: 'internacional', divisao: 1 });
  // rodadas de liga já jogadas (as das copas ficam de fora)
  const reservadas = new Set([...(e.copa?.slots ?? []), ...(e.libertadores?.slots ?? []), ...(e.sulamericana?.slots ?? [])]);
  let jogadas = e.rodada;
  for (const s of reservadas) if (s < e.rodada) jogadas--;
  if (e.acesso) {
    for (const d of [1, 2] as const) {
      const clubes = clubesDaDivisao(e, d);
      if (clubes.length >= 2 && jogadas === rodadasDaLiga(clubes.length)) {
        const t = tabela(e, d);
        darTitulo(e, t[0]?.clubeId, { temporada: ano, competicao: nomeDaDivisao(d), tipo: 'nacional', divisao: d });
        pagarPremiosDaLiga(e, `br-${d}`, t, d === 2);
      }
    }
    if (e.mundo) {
      const segundas = new Set(DIVISOES_MUNDO.map((d) => d.segunda));
      for (const liga of ligasDosClubes(e.clubes)) {
        const n = e.clubes.filter((c) => c.liga === liga).length;
        if (jogadas === rodadasDaLiga(n)) {
          const t = tabelaDaLiga(e, liga);
          darTitulo(e, t[0]?.clubeId, { temporada: ano, competicao: nomeDaLiga(liga), tipo: 'nacional', divisao: segundas.has(liga) ? 2 : 1 });
          pagarPremiosDaLiga(e, liga, t, segundas.has(liga));
        }
      }
    }
  } else if (jogadas === rodadasDeLiga(e) && e.rodada >= 1) {
    const minha = divisaoDoHumano(e);
    const t = tabela(e);
    darTitulo(e, t[0]?.clubeId, { temporada: ano, competicao: e.ligaMundo && e.nomeLiga ? e.nomeLiga : nomeDaDivisao(minha), tipo: 'nacional', divisao: minha });
    pagarPremiosDaLiga(e, `br-${minha}`, t, minha === 2);
  }
}

/** Joga a próxima rodada: simula as partidas, aplica cartões, lesões, energia e finanças. */
export function simularRodada(e: EstadoJogo, intervencoes: Record<number, IntervencaoPartida[]> = {}, opcoes: { acrescimo1?: number } = {}): RodadaJogada {
  if (temporadaEncerrada(e)) throw new Error('temporada encerrada: chame encerrarTemporada');
  definirModoHabilidade(true);
  const rng = new Rng(e.rngEstado);
  const confrontos = e.calendario[e.rodada] as Calendario[number];
  const rodadaLib = ehRodadaDeLibertadores(e, e.rodada);
  const rodadaDeCopa = ehRodadaDeCopa(e, e.rodada) || rodadaLib;
  const partidas: PartidaJogada[] = [];

  // cartões valem por campeonato: nas rodadas da copa (no calendário misto) contam os da copa, nas outras os da liga
  const copaJunta = rodadaDeCopa && !rodadaLib && e.formato !== 'copa';
  const suspensao = (j: Jogador): number => (rodadaLib ? j.suspLib : copaJunta ? j.suspCopa : j.suspLiga) ?? 0;
  const definirSuspensao = (j: Jogador, n: number): void => {
    if (rodadaLib) j.suspLib = n;
    else if (copaJunta) j.suspCopa = n;
    else j.suspLiga = n;
  };
  const clubesDaRodadaAtual = new Set(confrontos.flatMap((cf) => [cf.mandante, cf.visitante]));
  // quem já estava suspenso para este campeonato cumpre a suspensão agora (o suspenso de hoje só começa a cumprir na próxima)
  const cumprindo: Jogador[] = [];
  for (const c of clubesComExternos(e)) {
    for (const j of c.elenco) {
      if (j.lesaoRodadas > 0) j.lesaoRodadas--;
      if (clubesDaRodadaAtual.has(c.id) && suspensao(j) > 0) cumprindo.push(j);
    }
  }

  // juros do empréstimo bancário são cobrados quando o mês do calendário muda
  const mesAtual = dataDaRodada(e.temporada, e.rodada).getUTCMonth();
  const mesAnterior = e.rodada > 0 ? dataDaRodada(e.temporada, e.rodada - 1).getUTCMonth() : mesAtual;
  if (mesAtual !== mesAnterior) for (const c of e.clubes) cobrarJuros(c);

  const renovadosHumano: string[] = [];
  const jogadaTreino: string[] = [];
  const jogaram = new Set<number>();
  for (const conf of confrontos) {
    const m = clubePorId(e, conf.mandante);
    const v = clubePorId(e, conf.visitante);
    // cada partida tem o próprio sorteio: refazer um jogo (intervenção de técnico no online) não muda os outros
    const rngJogo = new Rng(rng.inteiro(2_147_483_647));
    // item Bicho da loja: +20% de força em todos os jogadores do clube só nesta partida (volta ao normal logo depois e o bônus se consome)
    const desfazerBonus = [aplicarBonusPartida(m), aplicarBonusPartida(v)];
    const resultado = simularPartida(m, v, rngJogo, { intervencoes: [intervencoes[m.id] ?? [], intervencoes[v.id] ?? []], acrescimo1: opcoes.acrescimo1 });
    for (const desfazer of desfazerBonus) desfazer();
    const bilh = calcularBilheteria(m, v, rng);

    for (const p of resultado.participacoes) {
      const clube = p.lado === 0 ? m : v;
      const j = jogadorPorId(clube, p.jogadorId);
      if (!j) continue;
      jogaram.add(j.id);
      j.jogos++;
      j.gols += p.gols;
      j.assistencias += p.assistencias;
      j.notaSoma += p.nota;
      // o cansaço vem do jogo: a energia que ele tinha ao terminar ou sair (pressão alta e marcação forte cobram o preço); sem ela, o custo fixo
      j.energia = Math.max(30, p.energiaFim !== undefined ? Math.min(j.energia, p.energiaFim) : j.energia - (p.titular ? ENERGIA_CUSTO_TITULAR : ENERGIA_CUSTO_RESERVA));
      // 3 amarelos no mesmo campeonato: 1 jogo de suspensão nele; vermelho: 1 a 3 jogos nele
      let amarelos = (rodadaLib ? j.amarelosLib : copaJunta ? j.amarelosCopa : j.amarelosLiga) ?? 0;
      amarelos += p.amarelos;
      if (amarelos >= 3) {
        amarelos = 0;
        definirSuspensao(j, Math.max(suspensao(j), 1));
      }
      if (rodadaLib) j.amarelosLib = amarelos;
      else if (copaJunta) j.amarelosCopa = amarelos;
      else j.amarelosLiga = amarelos;
      if (p.vermelho) definirSuspensao(j, Math.max(suspensao(j), rngJogo.faixa(1, 3)));
      if (p.lesaoRodadas > 0) j.lesaoRodadas = p.lesaoRodadas;
    }

    atualizarConfianca(m, resultado.gols[0], resultado.gols[1]);
    atualizarConfianca(v, resultado.gols[1], resultado.gols[0]);
    m.caixa += bilh.renda;
    m.balanco.bilheteria += bilh.renda;
    e.resultados.push({ rodada: e.rodada, mandante: m.id, visitante: v.id, gols: resultado.gols });
    partidas.push({ mandante: m.id, visitante: v.id, resultado, publico: bilh.total, renda: bilh.renda });
  }

  for (const c of e.clubes) {
    for (const j of c.elenco) {
      if (!jogaram.has(j.id)) j.energia = Math.min(100, j.energia + ENERGIA_RECUPERACAO + 4);
      else j.energia = Math.min(100, j.energia + ENERGIA_RECUPERACAO);
    }
    // jogo só de copa: folha e patrocínio da copa em cada rodada; copa junto com a liga: salários e patrocínio correm só nas
    // rodadas da liga (a copa paga só os prêmios das fases)
    const soCopa = e.formato === 'copa';
    const nLiga = rodadasDeLiga(e);
    const folha = soCopa ? folhaPorRodada(c, RODADAS_COPA, MESES_DE_FOLHA_COPA) : rodadaDeCopa ? 0 : folhaPorRodada(c, nLiga);
    const patrocinio = soCopa ? Math.round(PATROCINIO_COPA / RODADAS_COPA) : rodadaDeCopa ? 0 : Math.round(PATROCINIO_TEMPORADA / nLiga);
    c.caixa += patrocinio - folha;
    c.balanco.patrocinio += patrocinio;
    c.balanco.salarios += folha;
    const evolucoes = treinarClube(c, rng);
    if (c.humano && evolucoes.length > 0) (jogadaTreino.push(...evolucoes));
    const renovados = passarDiasDeContrato(c);
    if (c.humano && renovados.length > 0) renovadosHumano.push(...renovados.map((j) => j.nome));
    evoluirJuniores(c);
  }

  // quem estava suspenso cumpriu esta rodada (quem foi expulso nela ainda não: o contador dele só cai na próxima do campeonato)
  for (const j of cumprindo) definirSuspensao(j, Math.max(0, suspensao(j) - 1));

  const jogada: RodadaJogada = { temporada: e.temporada, rodada: e.rodada, partidas };
  if (renovadosHumano.length > 0) jogada.contratosRenovados = renovadosHumano;
  if (jogadaTreino.length > 0) jogada.treino = jogadaTreino.slice(0, 12);
  // jogadores dos clubes estrangeiros da Libertadores também cansam e se recuperam (fora de e.clubes, não entram no laço acima)
  for (const c of e.libertadores?.clubesExternos ?? []) {
    for (const j of c.elenco) j.energia = Math.min(100, j.energia + (jogaram.has(j.id) ? ENERGIA_RECUPERACAO : ENERGIA_RECUPERACAO + 4));
  }

  e.ultimaRodada = jogada;
  e.rodada++;
  if (e.copa) avancarCopa(e, rng);
  if (e.libertadores) avancarLibertadores(e, rng);
  concederTitulosConcluidos(e);
  // as ligas estrangeiras jogam a rodada delas junto com cada rodada de liga do Brasileirão (não nas da Copa nem da Libertadores)
  if (e.ligasMundoVivas && !rodadaDeCopa && e.formato !== 'copa') avancarLigasMundo(e);
  sincronizarCartoes(e);
  mercadoIA(e, rng);
  e.rngEstado = rng.estado;
  return jogada;
}

/** Variação anual de overall conforme a idade. */
function evoluir(j: Jogador, rng: Rng): void {
  let delta: number;
  if (j.idade < 21) delta = rng.faixa(1, 5);
  else if (j.idade <= 24) delta = rng.faixa(0, 3);
  else if (j.idade <= 28) delta = rng.faixa(-1, 2);
  else if (j.idade <= 32) delta = -rng.faixa(0, 2);
  else delta = -rng.faixa(1, 4);
  j.overall = Math.min(95, Math.max(15, j.overall + delta));
}

/** Texto do fim da temporada: campeão, posição do técnico e, com acesso e rebaixamento, quem subiu e quem caiu. */
export function resumoTemporada(e: EstadoJogo, reg: HistoricoTemporada, paraTodos = false): string {
  const nome = (id: number): string => clubePorId(e, id).nome;
  let t = `Campeão da temporada ${reg.temporada}: ${nome(reg.campeaoId)}.${paraTodos ? '' : ` Você terminou em ${reg.posicaoHumano}º.`}`;
  if (reg.libCampeaoId !== undefined) t += ` Campeão da Libertadores: ${nome(reg.libCampeaoId)}.`;
  if (reg.sulCampeaoId !== undefined) t += ` Campeão da Sul-Americana: ${nome(reg.sulCampeaoId)}.`;
  if (reg.campeoes) t += ` Campeão da Série A: ${nome(reg.campeoes[0])}; da Série B: ${nome(reg.campeoes[1])}.`;
  if (reg.rebaixados && reg.promovidos) {
    t += ` Caíram para a Série B: ${reg.rebaixados.map(nome).join(', ')}. Subiram para a Série A: ${reg.promovidos.map(nome).join(', ')}.`;
    if (!paraTodos && reg.rebaixados.includes(e.humanoId)) t += ' Seu clube foi REBAIXADO para a Série B.';
    else if (!paraTodos && reg.promovidos.includes(e.humanoId)) t += ' Seu clube CONQUISTOU O ACESSO à Série A!';
  }
  if (!paraTodos && reg.rebMundo?.includes(e.humanoId)) t += ' Seu clube foi REBAIXADO para a divisão de baixo.';
  else if (!paraTodos && reg.promMundo?.includes(e.humanoId)) t += ' Seu clube CONQUISTOU O ACESSO à divisão de cima!';
  return t;
}

/**
 * Fim da temporada das ligas estrangeiras do jogo com o mundo inteiro: premia pela colocação (a divisão de acesso paga menos), dá o
 * troféu ao campeão de cada liga (o da liga do técnico é registrado por quem chama), guarda as classificações finais em
 * `e.ligasMundo` (de onde a Argentina manda os classificados às copas continentais) e faz o acesso e o rebaixamento.
 */
function encerrarLigasDoMundo(e: EstadoJogo, reg: HistoricoTemporada, ligaHumano: string | undefined): void {
  const segundas = new Set(DIVISOES_MUNDO.map((d) => d.segunda));
  const finais: Record<string, TabelaMundo> = {};
  const tabelas = new Map<string, LinhaTabela[]>();
  for (const liga of ligasDosClubes(e.clubes)) {
    const t = tabelaDaLiga(e, liga);
    tabelas.set(liga, t);
    const segunda = segundas.has(liga);
    pagarPremiosDaLiga(e, liga, t, segunda);

    finais[liga] = {
      temporada: e.temporada,
      tabela: t.map((l) => ({ clube: clubePorId(e, l.clubeId).nome, pts: l.pts, v: l.v, e: l.e, d: l.d, gp: l.gp, gc: l.gc })),
    };
    const campeao = t[0];
    if (campeao && liga !== ligaHumano) registrarTrofeu(clubePorId(e, campeao.clubeId), { temporada: e.temporada, competicao: nomeDaLiga(liga), tipo: 'nacional', divisao: segunda ? 2 : 1 });
  }
  e.ligasMundo = finais;
  const rebaixados: number[] = [];
  const promovidos: number[] = [];
  for (const d of DIVISOES_MUNDO) {
    const cima = tabelas.get(d.primeira);
    const baixo = tabelas.get(d.segunda);
    if (!cima || !baixo) continue;
    const caem = cima.slice(-d.vagas).map((l) => l.clubeId);
    const sobem = baixo.slice(0, d.vagas).map((l) => l.clubeId);
    for (const id of caem) clubePorId(e, id).liga = d.segunda;
    for (const id of sobem) clubePorId(e, id).liga = d.primeira;
    rebaixados.push(...caem);
    promovidos.push(...sobem);
  }
  reg.rebMundo = rebaixados;
  reg.promMundo = promovidos;
}

/** Premia, envelhece o elenco e prepara a próxima temporada. */
export function encerrarTemporada(e: EstadoJogo): HistoricoTemporada {
  if (!temporadaEncerrada(e)) throw new Error('a temporada ainda não terminou');
  const rng = new Rng(e.rngEstado);
  const ehCopa = e.formato === 'copa';
  const foto = fotografarTemporada(e);
  // campeonatos internacionais: a temporada de cada liga estrangeira (a da Argentina decide 4 vagas da Libertadores seguinte)
  if (!e.ligaMundo && !e.mundo && !finalizarLigasMundo(e)) simularLigasMundo(e);
  // vagas do Brasil na Libertadores da próxima temporada, pela classificação final (antes de subirem e caírem clubes)
  // Saves e salas que ainda não tinham a Libertadores ganham a primeira edição na virada (Brasileirão A+B com Copa do Brasil); nela o campeão da Copa do Brasil e os 4 primeiros da Série A têm vaga garantida.
  const entraLibertadores = !ehCopa && (Boolean(e.libertadores) || (Boolean(e.acesso) && temCopaJunta(e) && !e.semLibertadores));
  const vagasLibertadores = entraLibertadores ? brasileirosQualificados(e, tabela(e, e.acesso ? 1 : undefined)) : null;
  let campeao: number;
  let reg: HistoricoTemporada;
  let titulo: Trofeu;
  if (ehCopa) {
    // prêmios da copa já foram pagos a cada fase concluída
    campeao = e.copa?.campeaoId ?? -1;
    if (campeao < 0) throw new Error('a copa ainda não tem campeão');
    const campanha = campanhaNaCopa(e, e.humanoId);
    reg = { temporada: e.temporada, campeaoId: campeao, posicaoHumano: campanha.posicao, competicao: COPA_NOME, fase: campanha.texto };
    titulo = { temporada: e.temporada, competicao: COPA_NOME, tipo: 'copa', divisao: 1 };
  } else {
    const minha = divisaoDoHumano(e);
    // as duas divisões (quando há acesso e rebaixamento) são premiadas e têm campeão; a Série B paga menos
    const divisoes: DivisaoClube[] = e.acesso ? [1, 2] : [minha];
    const tabelas = new Map<DivisaoClube, LinhaTabela[]>();
    for (const d of divisoes) {
      const t = tabela(e, d);
      tabelas.set(d, t);
      // o campeão da Série A leva, além do prêmio da colocação, o prêmio do título
      pagarPremiosDaLiga(e, `br-${d}`, t, d === 2);

    }
    // técnico num clube estrangeiro (jogo com o mundo inteiro): a "minha" classificação é a da liga dele, não a do Brasileirão
    const ligaHumano = ligaDoHumano(e);
    const classificacao = ligaHumano ? tabelaDaLiga(e, ligaHumano) : (tabelas.get(minha) as LinhaTabela[]);
    campeao = (classificacao[0] as LinhaTabela).clubeId;
    const posicaoHumano = classificacao.findIndex((l) => l.clubeId === e.humanoId) + 1;
    reg = { temporada: e.temporada, campeaoId: campeao, posicaoHumano };
    titulo = ligaHumano
      ? { temporada: e.temporada, competicao: nomeDaLiga(ligaHumano), tipo: 'nacional', divisao: DIVISOES_MUNDO.some((d) => d.segunda === ligaHumano) ? 2 : 1 }
      : { temporada: e.temporada, competicao: e.ligaMundo && e.nomeLiga ? e.nomeLiga : nomeDaDivisao(minha), tipo: 'nacional', divisao: minha };
    if (e.mundo) encerrarLigasDoMundo(e, reg, ligaHumano);
    if (e.acesso) {
      const [ta, tb] = [tabelas.get(1) as LinhaTabela[], tabelas.get(2) as LinhaTabela[]];
      reg.campeoes = [(ta[0] as LinhaTabela).clubeId, (tb[0] as LinhaTabela).clubeId];
      // o campeão da outra divisão também ganha o troféu dele (técnico no exterior: os dois campeões brasileiros)
      const outras: DivisaoClube[] = ligaHumano ? [1, 2] : [minha === 1 ? 2 : 1];
      for (const outra of outras) {
        const campeaoOutra = (tabelas.get(outra) as LinhaTabela[])[0] as LinhaTabela;
        registrarTrofeu(clubePorId(e, campeaoOutra.clubeId), { temporada: e.temporada, competicao: nomeDaDivisao(outra), tipo: 'nacional', divisao: outra });
      }
      // acesso e rebaixamento: os 4 últimos da Série A caem e os 4 primeiros da Série B sobem
      const caem = ta.slice(-VAGAS_ACESSO).map((l) => l.clubeId);
      const sobem = tb.slice(0, VAGAS_ACESSO).map((l) => l.clubeId);
      for (const id of caem) clubePorId(e, id).divisao = 2;
      for (const id of sobem) clubePorId(e, id).divisao = 1;
      reg.rebaixados = caem;
      reg.promovidos = sobem;
    }
  }
  registrarTrofeu(clubePorId(e, campeao), titulo);
  if (campeao === e.humanoId) registrarTrofeuTecnico(e, titulo);
  if (temCopaJunta(e)) {
    // a Copa do Brasil do mesmo save: o título dela vai para a galeria e o resumo da temporada guarda a campanha do clube humano
    const campanha = campanhaNaCopa(e, e.humanoId);
    reg.copaCampeaoId = e.copa?.campeaoId ?? undefined;
    reg.copaFase = campanha.texto;
    const campeaoCopa = e.copa?.campeaoId;
    if (campeaoCopa != null) {
      const tituloCopa: Trofeu = { temporada: e.temporada, competicao: COPA_NOME, tipo: 'copa', divisao: 1 };
      registrarTrofeu(clubePorId(e, campeaoCopa), tituloCopa);
      if (campeaoCopa === e.humanoId) registrarTrofeuTecnico(e, tituloCopa);
    }
  }
  if (e.sulamericana) {
    const sul = e.sulamericana;
    reg.sulCampeaoId = sul.campeaoId ?? undefined;
    reg.sulFase = campanhaNaSulamericana(e, e.humanoId);
    if (sul.campeaoId != null) {
      const tituloSul: Trofeu = { temporada: e.temporada, competicao: SULAMERICANA_NOME, tipo: 'internacional', divisao: 1 };
      const campeaoSul = e.clubes.find((c) => c.id === sul.campeaoId);
      if (campeaoSul) registrarTrofeu(campeaoSul, tituloSul);
      if (sul.campeaoId === e.humanoId) registrarTrofeuTecnico(e, tituloSul);
    }
  }
  if (e.libertadores) {
    const lib = e.libertadores;
    reg.libCampeaoId = lib.campeaoId ?? undefined;
    reg.libFase = campanhaNaLibertadores(e, e.humanoId);
    if (lib.campeaoId != null) {
      const tituloLib: Trofeu = { temporada: e.temporada, competicao: LIBERTADORES_NOME, tipo: 'internacional', divisao: 1 };
      const campeaoLib = e.clubes.find((c) => c.id === lib.campeaoId);
      if (campeaoLib) registrarTrofeu(campeaoLib, tituloLib);
      if (lib.campeaoId === e.humanoId) registrarTrofeuTecnico(e, tituloLib);
    }
  }

  // quem subiu ou desceu muda a divisão que o jogo mostra como "a do técnico" (saves de uma divisão só seguem como estavam)
  if (e.acesso) e.divisao = divisaoDoHumano(e) === 2 ? 'b' : 'a';

  const proximoId = { valor: e.proximoIdJogador };
  for (const c of e.clubes) {
    virarAnoJuniores(c, rng, proximoId, nomeJogador, ELENCO_MAX);
    const novos: Jogador[] = [];
    for (const j of c.elenco) {
      j.idade++;
      evoluir(j, rng);
      if (j.idade >= 36 && rng.chance(0.5)) {
        // aposentadoria: entra um jovem da base na mesma posição
        const jovem = gerarJogador(proximoId.valor++, j.posicao, false, c.nivel, rng);
        jovem.contratoDias = contratoInicial(jovem.id);
        jovem.idade = rng.faixa(17, 19);
        jovem.overall = Math.max(15, jovem.overall - 4);
        jovem.salario = calcularSalario(jovem.overall, jovem.idade, jovem.posicao, c.nivel);
        jovem.valor = calcularValor(jovem.overall, jovem.idade, c.nivel, jovem.posicao);
        novos.push(jovem);
        continue;
      }
      j.salario = calcularSalario(j.overall, j.idade, j.posicao, c.nivel);
      j.valor = calcularValor(j.overall, j.idade, c.nivel, j.posicao);
      j.gols = 0;
      j.jogos = 0;
      j.assistencias = 0;
      j.notaSoma = 0;
      j.amarelosAcumulados = 0;
      j.lesaoRodadas = 0;
      j.suspensaoRodadas = 0;
      j.suspLiga = 0;
      j.suspCopa = 0;
      j.amarelosLiga = 0;
      j.amarelosCopa = 0;
      j.suspLib = 0;
      j.amarelosLib = 0;
      j.energia = 100;
      novos.push(j);
    }
    c.elenco = novos;
    c.balanco = { bilheteria: 0, patrocinio: 0, premios: 0, salarios: 0, transferencias: 0, vendas: 0, compras: 0, estadio: 0, multas: 0, juros: 0 };
  }
  e.proximoIdJogador = proximoId.valor;
  // elencos dos clubes estrangeiros da Libertadores: envelhecem e evoluem como os outros (sem base nem aposentadoria)
  for (const c of e.libertadores?.clubesExternos ?? []) {
    for (const j of c.elenco) {
      j.idade++;
      evoluir(j, rng);
      j.gols = 0;
      j.jogos = 0;
      j.assistencias = 0;
      j.notaSoma = 0;
      j.amarelosAcumulados = 0;
      j.lesaoRodadas = 0;
      j.suspensaoRodadas = 0;
      j.suspLiga = 0;
      j.suspCopa = 0;
      j.suspLib = 0;
      j.amarelosLiga = 0;
      j.amarelosCopa = 0;
      j.amarelosLib = 0;
      j.energia = 100;
    }
  }

  // jogadores livres envelhecem; os muito velhos se aposentam e o estoque é reposto
  e.livres = e.livres.filter((j) => (j.idade += 1) < 36);
  reporLivres(e, rng);

  Object.assign(reg, foto);
  e.historico.push(reg);
  e.temporada++;
  e.premiosPagos = [];
  if (!ehCopa && !e.mundo && (e.acesso || e.ligasMundoVivas)) iniciarLigasMundoVivas(e);
  e.rodada = 0;
  e.resultados = [];
  e.ultimaRodada = null;
  if (ehCopa) iniciarCopa(e, rng);
  else {
    const copaJunta = temCopaJunta(e);
    e.calendario = calendarioDeLiga(e, rng);
    if (copaJunta) {
      const extras = iniciarCopaJunta(e, rng, e.calendario, vagasLibertadores ? CORTES_CONTINENTAIS : []);
      if (vagasLibertadores && extras.length > 0) iniciarContinentais(e, rng, dividirVagas(vagasLibertadores), extras);
    }
  }
  sincronizarCartoes(e);
  e.rngEstado = rng.estado;
  return reg;
}

// ---- Save --------------------------------------------------------------------------------------

/**
 * Campos do jogador que ficam em 0 na maior parte do elenco: o save só guarda o que mudou (valor diferente de 0) e o carregamento
 * devolve o 0. As suspensões e amarelos "da vez" (suspensaoRodadas/amarelosAcumulados) são recalculados de qualquer jeito por
 * `sincronizarCartoes` a partir dos contadores por competição.
 */
const CAMPOS_ZERO_JOGADOR = [
  'lesaoRodadas',
  'suspensaoRodadas',
  'amarelosAcumulados',
  'gols',
  'jogos',
  'assistencias',
  'notaSoma',
  'amarelosLiga',
  'suspLiga',
  'suspCopa',
  'amarelosCopa',
  'suspLib',
  'amarelosLib',
] as const;
const CONTADORES_COMPETICAO: readonly string[] = ['amarelosLiga', 'suspLiga', 'suspCopa', 'amarelosCopa', 'suspLib', 'amarelosLib'];
const SET_CAMPOS_ZERO =new Set<string>(CAMPOS_ZERO_JOGADOR);

export function salvarJson(e: EstadoJogo): string {
  return JSON.stringify(e, function (this: Record<string, unknown>, chave: string, valor: unknown) {
    return valor === 0 && SET_CAMPOS_ZERO.has(chave) && 'overall' in this && 'posicao' in this ? undefined : valor;
  });
}

/** Devolve os zeros que o save compacto omite. */
function restaurarZeros(dados: Partial<EstadoJogo>): void {
  const lotes: Jogador[][] = [];
  const clubes = [...(dados.clubes ?? []), ...(dados.libertadores?.clubesExternos ?? [])] as Clube[];
  for (const c of clubes) {
    if (Array.isArray(c.elenco)) lotes.push(c.elenco);
    if (Array.isArray(c.juniores)) lotes.push(c.juniores as Jogador[]);
  }
  if (Array.isArray(dados.livres)) lotes.push(dados.livres);
  for (const lote of lotes) {
    for (const j of lote) {
      if (typeof j !== 'object' || j === null) continue;
      const r = j as unknown as Record<string, unknown>;
      // sem nenhum contador por competição (save antigo ou tudo zerado), deixa para `sincronizarCartoes` montá-los a partir de suspensaoRodadas
      const semBuckets = CONTADORES_COMPETICAO.every((k) => r[k] === undefined);
      for (const k of CAMPOS_ZERO_JOGADOR) if (r[k] === undefined && !(semBuckets && CONTADORES_COMPETICAO.includes(k))) r[k] = 0;
    }
  }
}

/** Sobe um save antigo para a versão atual, preenchendo o que não existia. */
function migrar(dados: Partial<EstadoJogo> & { versao?: number }): void {
  if (dados.versao === 1) {
    for (const c of (dados.clubes ?? []) as Clube[]) {
      c.tatica = TATICA_PADRAO();
      if (c.balanco && c.balanco.transferencias === undefined) c.balanco.transferencias = 0;
    }
    dados.livres = [];
    dados.transferencias = [];
    dados.versao = 2;
    // saves antigos não tinham jogadores livres: cria o estoque inicial com o gerador do próprio save
    if (typeof dados.rngEstado === 'number' && typeof dados.proximoIdJogador === 'number') {
      const rng = new Rng(dados.rngEstado);
      reporLivres(dados as EstadoJogo, rng);
      dados.rngEstado = rng.estado;
    }
  }
  if (dados.versao === 2) {
    const padraoPorPosicao: Record<string, [number, number]> = { G: [0, 3], Z: [7, 5], L: [10, 6], M: [4, 11], A: [9, 5] };
    const completar = (j: Jogador): void => {
      if (j.assistencias === undefined) j.assistencias = 0;
      if (j.notaSoma === undefined) j.notaSoma = 0;
      if (j.lado === undefined) j.lado = j.id % 2 === 0 ? 'E' : 'D';
      if (j.caracteristicas === undefined) j.caracteristicas = padraoPorPosicao[j.posicao] ?? [9, 11];
    };
    for (const c of (dados.clubes ?? []) as Clube[]) {
      c.elenco.forEach(completar);
      c.tatica = { ...TATICA_PADRAO(), ...c.tatica };
      if (c.tatica.marcacao === undefined) c.tatica.marcacao = 'leve';
      if (c.tatica.ataque === undefined) c.tatica.ataque = 'meio';
      if (c.confiancaDiretoria === undefined) c.confiancaDiretoria = 80;
      if (c.confiancaTorcida === undefined) c.confiancaTorcida = 75;
      if (c.usarPrecosSugeridos === undefined) c.usarPrecosSugeridos = true;
    }
    (dados.livres ?? []).forEach(completar);
    // o último resumo de rodada de um save antigo não tem os campos novos (minuto, notas)
    dados.ultimaRodada = null;
    dados.versao = 3;
  }
  if (dados.versao === 3) {
    const rng = new Rng(typeof dados.rngEstado === 'number' ? dados.rngEstado : 1);
    const proximoId = { valor: typeof dados.proximoIdJogador === 'number' ? dados.proximoIdJogador : 1_000_000 };
    for (const c of (dados.clubes ?? []) as Clube[]) {
      for (const j of c.elenco) if (j.contratoDias === undefined) j.contratoDias = contratoInicial(j.id);
      c.emprestimo ??= 0;
      c.trofeus ??= [];
      c.juniores ??= [];
      completarJuniores(c, JUNIORES_INICIAIS, rng, proximoId, nomeJogador);
    }
    dados.trofeusTecnico ??= [];
    dados.proximoIdJogador = proximoId.valor;
    dados.rngEstado = rng.estado;
    dados.versao = 4;
  }
}

/** Chaves que nunca fazem parte de um save e que, em mãos erradas, tentam mexer nos protótipos dos objetos. */
const CHAVES_PROIBIDAS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Lê e valida um save. `externo` = veio de fora (arquivo importado, conta na nuvem): além da validação, descarta o resumo da
 * última rodada, que só serve ao online e é a parte mais complexa de um save para conferir.
 */
export function carregarJson(texto: string, opcoes: { externo?: boolean } = {}): EstadoJogo {
  const dados = JSON.parse(texto, (chave, valor: unknown) => (CHAVES_PROIBIDAS.has(chave) ? undefined : valor)) as Partial<EstadoJogo>;
  if (typeof dados !== 'object' || dados === null || Array.isArray(dados)) throw new Error('save inválido');
  restaurarZeros(dados);
  migrar(dados);
  if (dados.versao !== VERSAO_SAVE) throw new Error(`versão de save não suportada: ${String(dados.versao)}`);
  validarSave(dados);
  if (opcoes.externo) dados.ultimaRodada = null;
  sincronizarCartoes(dados as EstadoJogo);
  return dados as EstadoJogo;
}
